/**
 * In-Store Only Badge Checker
 * 
 * Automatically checks and displays "in-store only" badges for products
 * Uses MutationObserver to detect dynamically added badges (e.g., in cart drawer)
 * Caches API responses to avoid redundant calls
 */

(function() {
  'use strict';

  const STOREFRONT_TOKEN = 'f03bcbe287205c23fd45fdd8609eefb1';
  const CHECKED_ATTR = 'data-in-store-only-checked';
  const PROCESSING_ATTR = 'data-in-store-only-processing';
  
  // Shared cache across all instances
  const productStockCache = window.productStockCache || new Map();
  window.productStockCache = productStockCache;

  function normalizeLocationName(name) {
    if (!name) return '';
    const normalized = name.toLowerCase().trim();
    if (normalized.includes('hartville')) return 'hartville';
    if (normalized.includes('middlefield')) return 'middlefield';
    if (normalized.includes('shop')) return 'shop';
    return normalized;
  }

  async function fetchProductVariants(productId) {
    if (productStockCache.has(productId)) {
      return productStockCache.get(productId);
    }

    const query = `
      query ProductVariants($productId: ID!) {
        product(id: $productId) {
          id
          variants(first: 100) {
            edges {
              node {
                id
                storeAvailability(first: 20) {
                  nodes {
                    location { id name }
                    available
                    quantityAvailable
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const productGid = `gid://shopify/Product/${productId}`;
      const res = await fetch(`/api/2025-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
        },
        body: JSON.stringify({
          query,
          variables: { productId: productGid },
        }),
      });

      const json = await res.json();
      if (json.errors) {
        console.error('Storefront API error:', json.errors);
        return null;
      }

      const variants = json?.data?.product?.variants?.edges || [];
      const variantData = variants.map((edge) => ({
        id: edge.node.id,
        storeAvailability: edge.node.storeAvailability.nodes || [],
      }));

      productStockCache.set(productId, variantData);
      return variantData;
    } catch (error) {
      console.error('Error fetching product variants:', error);
      return null;
    }
  }

  /**
   * Check if a location has tracked inventory with stock
   * Only considers stock if inventory is being tracked (quantityAvailable !== null)
   * If inventory is not tracked (quantityAvailable === null), it means the location
   * doesn't track inventory for this product, so we don't consider it as having stock
   * 
   * @param {Object} availability - Store availability object
   * @returns {boolean} - true if inventory is tracked AND has stock > 0
   */
  function hasTrackedStock(availability) {
    // Only consider stock if inventory is being tracked
    // If quantityAvailable is null, inventory is NOT tracked for this location
    // If quantityAvailable is 0, inventory IS tracked but out of stock
    // If quantityAvailable > 0, inventory IS tracked and has stock
    return availability.quantityAvailable !== null && availability.quantityAvailable > 0;
  }

  async function checkIsInStoreOnly(productId) {
    if (!productId) return false;

    const variantData = await fetchProductVariants(productId);
    if (!variantData || variantData.length === 0) {
      return false;
    }

    let hasHartvilleStock = false;
    let hasMiddlefieldStock = false;
    let hasTrackedShopInventory = false;
    let sawShopLocation = false;

    variantData.forEach((variant) => {
      variant.storeAvailability.forEach((availability) => {
        const locationName = normalizeLocationName(availability.location.name);
        
        // Only consider stock if inventory is tracked in this location
        // If quantityAvailable is null, the location doesn't track inventory for this product
        const hasStock = hasTrackedStock(availability);

        if (locationName === 'hartville' && hasStock) {
          hasHartvilleStock = true;
        } else if (locationName === 'middlefield' && hasStock) {
          hasMiddlefieldStock = true;
        } else if (locationName === 'shop') {
          sawShopLocation = true;
          if (availability.quantityAvailable !== null) {
            hasTrackedShopInventory = true;
          }
        }
      });
    });

    const isShopInventoryNull = !sawShopLocation || !hasTrackedShopInventory;

    return (hasHartvilleStock || hasMiddlefieldStock) && isShopInventoryNull;
  }

  async function processBadge(badgeElement) {
    // Skip if already checked or currently processing
    if (badgeElement.hasAttribute(CHECKED_ATTR) || badgeElement.hasAttribute(PROCESSING_ATTR)) {
      return;
    }

    const productId = badgeElement.getAttribute('data-product-id');
    if (!productId) return;

    // Mark as processing to avoid duplicate checks
    badgeElement.setAttribute(PROCESSING_ATTR, 'true');

    try {
      const isInStoreOnly = await checkIsInStoreOnly(parseInt(productId, 10));
      if (isInStoreOnly) {
        badgeElement.style.display = 'flex';
        // Also show container if it exists
        const container = badgeElement.closest('.cart-badge__container');
        if (container) {
          container.style.display = 'flex';
        }
      }
    } catch (error) {
      console.error('Error processing badge:', error);
    } finally {
      // Mark as checked and remove processing flag
      badgeElement.setAttribute(CHECKED_ATTR, 'true');
      badgeElement.removeAttribute(PROCESSING_ATTR);
    }
  }

  function checkAllBadges() {
    // Find all badges that haven't been checked yet
    const allBadges = document.querySelectorAll(`
      .badge-in-store-only[data-product-id]:not([${CHECKED_ATTR}]),
      .cart-badge__in-store-only[data-product-id]:not([${CHECKED_ATTR}])
    `);

    if (allBadges.length === 0) return;

    // Process badges in batches to avoid overwhelming the API
    const batchSize = 3;
    for (let i = 0; i < allBadges.length; i += batchSize) {
      const batch = Array.from(allBadges).slice(i, i + batchSize);
      batch.forEach(badge => processBadge(badge));
    }
  }

  // Initialize on DOM ready
  function init() {
    // Check existing badges
    checkAllBadges();

    // Use MutationObserver to detect dynamically added badges
    const observer = new MutationObserver((mutations) => {
      let shouldCheck = false;
      
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if the added node is a badge or contains badges
            if (node.matches && (
              node.matches('.badge-in-store-only[data-product-id]') ||
              node.matches('.cart-badge__in-store-only[data-product-id]') ||
              node.querySelector('.badge-in-store-only[data-product-id]') ||
              node.querySelector('.cart-badge__in-store-only[data-product-id]')
            )) {
              shouldCheck = true;
            }
          }
        });
      });

      if (shouldCheck) {
        // Debounce checks to avoid excessive API calls
        clearTimeout(window.inStoreOnlyBadgeCheckTimeout);
        window.inStoreOnlyBadgeCheckTimeout = setTimeout(() => {
          checkAllBadges();
        }, 200);
      }
    });

    // Observe the entire document for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also listen to cart update events as a fallback
    window.addEventListener('cart:updated', () => {
      setTimeout(checkAllBadges, 300);
    });

    window.addEventListener('cart:refresh', () => {
      setTimeout(checkAllBadges, 300);
    });
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
